/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, RadioGroup, Icon } from 'uxd-design-system-components';
import StyledModal from '../../components/StyledModal/StyledModal';
import './HouseholdInfo.scss';
import {
  AnswersContext,
  LocaleContext,
  QueryContext,
  PreviousPathContext,
} from '../../contexts';
import getStatus from '../../utils/getStatus/getStatus';
import getText from '../../assets/locale/locale';
import desanitizeString from '../../utils/sanitizeUtil/sanitizeUtil';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';
import ACTIONS from '../../constants/actions';

const HouseholdInfo = ({ setCurrentStep, schema, faInfo, sessionId }) => {
  const [localMaritalStatus, setLocalMaritalStatus] = useState(null);
  const [localDependentStatus, setLocalDependentStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [maritalStatusOptionsArr, setMaritalStatusOptionsArr] = useState([]);
  const [maritalStatSchema, setMaritalStatSchema] = useState([]);
  const [dependentsStatusOptionsArr, setDependentsStatusOptionsArr] = useState(
    []
  );
  const [dependentsSchema, setDependentsSchema] = useState([]);

  const { previousPath, setPreviousPath } = useContext(PreviousPathContext);
  const locId = useContext(LocaleContext);
  const history = useHistory();
  const location = useLocation();

  const { answers, answersDispatch } = useContext(AnswersContext);
  const { queryDispatch } = useContext(QueryContext);
  const { marital_status, dependents } = schema;

  const topOfHouseholdPage = useRef();

  const removeFocus = () => {
    const heading = document.getElementById('heading-no-focus');
    if (heading !== null) {
      heading.removeAttribute('tabIndex');
    }
  };

  useEffect(() => {
    setCurrentStep(window.location.pathname);
    document.title = getText(locId, 'HouseholdInfo', 'pageTitle');
    topOfHouseholdPage.current.focus();
    setTimeout(removeFocus, 1500);

    addDataLayerTrigger('dataLayer-initialized', {
      page_type: 'householdPage',
      locale: locId,
      environment: window.ENVIRONMENT,
      sessionid: sessionId ?? undefined,
      fa_no: faInfo.faNo ?? undefined,
      fa_entity_id: faInfo.entityIdBase64 ?? undefined,
    });
    addDataLayerTrigger('VirtualPageview', {
      page_path: window.location.pathname,
      page_title: document.title,
      page_referrer: previousPath,
      external_referrer:
        window.document.referrer && window.document.referrer.trim() !== ''
          ? window.document.referrer
          : undefined,
      page_url: window.location.href,
    });
  }, []);

  useEffect(() => {
    // if the form has already been filled, set the local value with the previously filled value

    if (answers.marriedAnswer) {
      setLocalMaritalStatus(answers.marriedAnswer);
    }

    if (answers.dependentsAnswer) {
      setLocalDependentStatus(answers.dependentsAnswer);
    }
  }, []);

  useEffect(() => {
    // sort schema and extract labels

    if (marital_status) {
      setMaritalStatSchema(marital_status);
      const newMaritalStatusOptionsArr = [];
      marital_status.forEach((statusValue) => {
        const newStatusValue = {
          id: statusValue.value,
          label: desanitizeString(statusValue.label),
          value: desanitizeString(statusValue.label),
          checked: statusValue.value === answers.marriedAnswer,
        };
        newMaritalStatusOptionsArr.push(newStatusValue);
      });
      setMaritalStatusOptionsArr(newMaritalStatusOptionsArr);
    }

    if (dependents) {
      setDependentsSchema(dependents);
      const newDependentsStatusOptionsArr = [];
      dependents.forEach((statusValue) => {
        const newStatusValue = {
          id: statusValue.value,
          label: desanitizeString(statusValue.label),
          value: desanitizeString(statusValue.label),
          checked: statusValue.value === answers.dependentsAnswer,
        };
        newDependentsStatusOptionsArr.push(newStatusValue);
      });
      setDependentsStatusOptionsArr(newDependentsStatusOptionsArr);
    }
  }, []);

  useEffect(() => {
    if (localMaritalStatus) {
      const newMaritalStatusOptionsArr = [];
      maritalStatusOptionsArr.forEach((statusValue) => {
        const isChecked = statusValue.value === localMaritalStatus;
        newMaritalStatusOptionsArr.push({
          ...statusValue,
          checked: isChecked,
        });
      });
      setMaritalStatusOptionsArr(newMaritalStatusOptionsArr);
    }
  }, [localMaritalStatus]);

  useEffect(() => {
    if (localDependentStatus) {
      const newDependentsStatusOptionsArr = [];
      dependentsStatusOptionsArr.forEach((statusValue) => {
        const isChecked = statusValue.value === localDependentStatus;
        newDependentsStatusOptionsArr.push({
          ...statusValue,
          checked: isChecked,
        });
      });
      setDependentsStatusOptionsArr(newDependentsStatusOptionsArr);
    }
  }, [localDependentStatus]);

  const handleMaritalStatusChange = (e) => {
    setLocalMaritalStatus(e);
  };

  const handleDependentStatusChange = (e) => {
    setLocalDependentStatus(e);
  };

  const advance = () => {
    addDataLayerTrigger('advance-to-hopes');
    setPreviousPath(window.location.pathname);
    queryDispatch({
      type: ACTIONS.SET_HOUSEHOLD_INFO,
      payload: {
        married: getStatus(localMaritalStatus, maritalStatSchema),
        dependents: getStatus(localDependentStatus, dependentsSchema),
      },
    });
    answersDispatch({
      type: ACTIONS.SET_HOUSEHOLD_INFO,
      payload: {
        married: localMaritalStatus,
        dependents: localDependentStatus,
      },
    });
    history.push({
      pathname: getText(locId, 'AllPages', 'goalsPath', 'App'),
      search: location.search,
    });
  };

  const goBack = () => {
    setPreviousPath(window.location.pathname);
    addDataLayerTrigger('go-back-to-personal');
    history.push({
      pathname: getText(locId, 'AllPages', 'personalPath', 'App'),
      search: location.search,
    });
  };

  return (
    <main className="form-view form-style">
      <div className="form-card">
        <fieldset className="field-set accessibility">
          <legend>
            <span className="accessibility">
              {getText(locId, 'HouseholdInfo', 'accessibilityFieldset')}
            </span>
          </legend>
          <div className="singlescreen">
            <div className="title">
              <h1 id="heading-no-focus" ref={topOfHouseholdPage} tabIndex="-1">
                {getText(locId, 'HouseholdInfo', 'header')}
              </h1>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
              <div
                role="button"
                className="why-ask-button"
                onClick={() => {
                  setShowModal(true);
                  addDataLayerTrigger('household-why-ask-button');
                }}
                onKeyDown={(event) => {
                  if (event.keyCode === 13 || event.keyCode === 32) {
                    setShowModal(true);
                    addDataLayerTrigger('household-why-ask-button');
                  }
                }}
                tabIndex="0"
              >
                <Icon name="help-question-mark" />
                {getText(locId, 'HouseholdInfo', 'modalHeader')}
              </div>
              <StyledModal
                headerText={getText(locId, 'HouseholdInfo', 'modalHeader')}
                paragraphText={getText(
                  locId,
                  'HouseholdInfo',
                  'modalParagraph'
                )}
                buttonText={getText(locId, 'HouseholdInfo', 'modalButton')}
                displayModal={showModal}
                closeModalHandler={() => setShowModal(false)}
              />
            </div>
            <RadioGroup
              name="maritalStatusGroup"
              options={maritalStatusOptionsArr}
              legend={getText(locId, 'HouseholdInfo', 'maritalLegend')}
              onChange={(e) => {
                handleMaritalStatusChange(e);
                addDataLayerTrigger('household-radio-button-marital', {
                  'household-marital-selection': e,
                });
              }}
            />
            <RadioGroup
              name="dependentStatusGroup"
              options={dependentsStatusOptionsArr}
              legend={getText(locId, 'HouseholdInfo', 'dependentLegend')}
              onChange={(e) => {
                handleDependentStatusChange(e);
                addDataLayerTrigger('household-radio-button-dependents', {
                  'household-dependents-selection': e,
                });
              }}
            />
            <div className="household actions">
              <Button
                id="household-next-button"
                className="button button-primary"
                onClick={advance}
                type="submit"
                label={getText(locId, 'AllPages', 'nextButton')}
              />
              <Button
                id="household-back-button"
                className="button button-secondary"
                onClick={goBack}
                type="button"
                label={getText(locId, 'AllPages', 'backButton')}
              />
            </div>
          </div>
        </fieldset>
      </div>
    </main>
  );
};

HouseholdInfo.propTypes = {
  setCurrentStep: PropTypes.func.isRequired,
  schema: PropTypes.shape({
    dependents: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    employment_status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    hope: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    marital_status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    worry: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  }).isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string),
  sessionId: PropTypes.string,
};

HouseholdInfo.defaultProps = {
  faInfo: {
    faFirstName: null,
    faLastName: null,
    branchNo: null,
    faNo: null,
    faPhoto: null,
  },
  sessionId: '',
};

export default HouseholdInfo;
